import classNames from 'classnames';
import { forwardRef } from 'react';

import { useFieldState, WithFieldStateProps } from '../../hooks';
import { getCurrentI18nLanguage } from '../../i18n';

export interface InputProps
  extends Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'size'>,
    WithFieldStateProps {
  size?: 'medium' | 'small';
}

/**
 * Prestyled input field with extended field states
 */
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const { className, type, disabled, submitting, error, dirty, size = 'medium', ...otherProps } = props;

  const fieldState = useFieldState(props);

  const { inputLang } = getCurrentI18nLanguage();

  return (
    <input
      {...otherProps}
      ref={ref}
      lang={type === 'number' ? inputLang : undefined}
      type={type}
      disabled={disabled || submitting}
      className={classNames(
        'appearance-none bg-surface-base border dark:bg-neutral-700 focus:outline-none focus:ring-offset-0 focus:ring-1 text-white', // prestyling
        type !== 'file'
          ? classNames(
              size === 'medium' && 'px-3 py-2 sm:text-sm',
              size === 'small' && ' px-2 py-1 text-xs leading-[normal]',
            )
          : undefined,
        'block w-full rounded-md shadow-sm',
        fieldState === 'default' &&
          'border-border focus:ring-brand-primary focus:border-brand-primary dark:border-neutral-800 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
        fieldState === 'dirty' &&
          'border-brand-primary focus:ring-brand-primary focus:border-brand-primary dark:border-sky-300 focus:dark:ring-cyan-500 focus:dark:border-cyan-500',
        fieldState === 'error' &&
          'border-red-500 focus:ring-red-500 focus:border-red-500 placeholder-red-300 text-red-800 dark:border-red-400 focus:dark:ring-red-400 focus:dark:border-red-400',
        fieldState === 'readOnly' &&
          'read-only:bg-neutral-50 read-only:border-neutral-50 read-only:placeholder-inherit read-only:focus:ring-0 read-only:cursor-default read-only:dark:bg-neutral-900 read-only:dark:border-neutral-900',
        fieldState === 'disabled' &&
          'disabled:opacity-100 disabled:shadow-none disabled:border-border disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-neutral-400 disabled:cursor-not-allowed disabled:dark:border-neutral-800',
        fieldState === 'disabledError' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-red-300 disabled:focus:ring-0 disabled:placeholder-red-200 disabled:text-red-400 disabled:cursor-not-allowed disabled:dark:border-red-200',
        fieldState === 'disabledDirty' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-primary-600/70 disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:cursor-not-allowed disabled:dark:border-sky-200',
        fieldState === 'submitting' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-border disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-neutral-800',
        fieldState === 'submittingDirty' &&
          'disabled:opacity-70 disabled:shadow-none disabled:border-primary-600/70 disabled:animate-pulse disabled:focus:ring-0 disabled:placeholder-gray-200 disabled:text-secondary disabled:dark:border-sky-200',

        type === 'file'
          ? classNames(
              'cursor-pointer',
              'text-sm',
              'text-gray-700',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:bg-cyan-600',
              '[&:hover::file-selector-button,_&::-webkit-file-upload-button]:bg-cyan-500',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:transition',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:ease-in-out',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:duration-150',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:border-0',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:border-none',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:text-white',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:text-sm',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:font-normal',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:px-5',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:py-2',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:my-0',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:ml-0',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:mr-3',
              '[&::file-selector-button,_&::-webkit-file-upload-button]:cursor-pointer',
            )
          : undefined,
        className,
      )}
    />
  );
});

export default Input;
